// The condition builder's model: Wrangler's condition tree (its DESIGN.md section 7) as editable
// rows and groups, and back. One connector per group (design decision 1): a group's `op` joins all
// its children. An imported quota whose join Alchemer didn't reveal has op 'unknown' at the top
// until a connector is clicked.
//
//   group      {id, type: 'group', op: 'and' | 'or' | 'unknown', children: [...]}
//   condition  {id, type: 'condition', questionId, operator, values}

let nextId = 1;
const id = () => `n${nextId++}`;

export const OPERATOR_LABELS = {
  in: 'is one of',
  not_in: 'answered, and not one of', // never "is not one of": Wrangler's not_in excludes blanks
  answered: 'is answered',
  not_answered: 'is not answered',
  eq: 'equals',
  gt: 'is greater than',
  gte: 'is at least',
  lt: 'is less than',
  lte: 'is at most',
  between: 'is between',
};

const ANSWERED = ['answered', 'not_answered'];
const OPERATORS_BY_KIND = {
  choice: ['in', 'not_in', ...ANSWERED],
  'multi-select': ['in', 'not_in', ...ANSWERED],
  numeric: ['eq', 'gt', 'gte', 'lt', 'lte', 'between', ...ANSWERED],
  text: ['in', 'not_in', ...ANSWERED],
};
const NUMERIC_OPERATORS = new Set(['eq', 'gt', 'gte', 'lt', 'lte', 'between']);

/** The operators a question allows, by its `kind` (decision 8: answered / not answered for every kind). */
export const operatorsFor = (question) => OPERATORS_BY_KIND[question?.kind] || [];

/** How many values an operator takes: 'many' (one or more), 1, 2 or 0. */
export const valueCount = (operator) => {
  if (operator === 'in' || operator === 'not_in') return 'many';
  if (operator === 'between') return 2;
  if (NUMERIC_OPERATORS.has(operator)) return 1;
  return 0;
};

export const newCondition = (questionId = null) => ({ id: id(), type: 'condition', questionId, operator: null, values: [] });
export const newGroup = (op = 'and', children = [newCondition()]) => ({ id: id(), type: 'group', op, children });

const toNode = (logic) => {
  if (logic && Array.isArray(logic.children)) {
    return { id: id(), type: 'group', op: logic.op, children: logic.children.map(toNode) };
  }
  return {
    id: id(), type: 'condition', questionId: logic?.question_id ?? null,
    operator: logic?.operator ?? null, values: [...(logic?.values || [])],
  };
};

/** Wrangler's tree as the builder's model. The top is always a group, so a lone condition can gain siblings. */
export const fromLogic = (logic, { joinUnknown = false } = {}) => {
  if (!logic) return newGroup('and', []);
  const node = toNode(logic);
  const top = node.type === 'group' ? node : { id: id(), type: 'group', op: 'and', children: [node] };
  return joinUnknown ? { ...top, op: 'unknown' } : top;
};

const toWrangler = (node) => {
  if (node.type === 'condition') {
    const out = { question_id: node.questionId, operator: node.operator };
    if (valueCount(node.operator) !== 0) out.values = node.values;
    return out;
  }
  return { op: node.op, children: node.children.map(toWrangler) };
};

/** The model as Wrangler's tree. A top group with one child is just that child. Validate first. */
export const toLogic = (tree) => {
  if (tree.children.length === 1) return toWrangler(tree.children[0]);
  return toWrangler(tree);
};

/**
 * Everything to fix before saving, as [{id, message}] (id of the row or group). Wrangler checks
 * again on save; this catches mistakes while they're in front of the user.
 */
export const validate = (tree, questionIndex) => {
  const problems = [];
  const visit = (node, depth) => {
    if (node.type === 'group') {
      if (node.op === 'unknown') problems.push({ id: node.id, message: 'Choose AND or OR: click a connector.' });
      if (node.children.length === 0) {
        problems.push({ id: node.id, message: depth === 0 ? 'Add at least one condition.' : 'This group is empty: add a condition or remove it.' });
      }
      node.children.forEach((child) => visit(child, depth + 1));
      return;
    }
    const question = questionIndex.get(node.questionId);
    if (node.questionId == null) return problems.push({ id: node.id, message: 'Choose a question.' });
    if (!question) return problems.push({ id: node.id, message: `Question ${node.questionId} isn't in the synced list.` });
    if (!question.usable_in_quotas) {
      return problems.push({ id: node.id, message: `This question can't be used: ${question.unusable_reason || 'not supported'}.` });
    }
    if (!node.operator) return problems.push({ id: node.id, message: 'Choose how to compare.' });
    if (!operatorsFor(question).includes(node.operator)) {
      return problems.push({ id: node.id, message: `"${OPERATOR_LABELS[node.operator] || node.operator}" doesn't apply to this question.` });
    }
    const count = valueCount(node.operator);
    if (count === 'many' && node.values.length === 0) problems.push({ id: node.id, message: 'Choose at least one answer.' });
    if (typeof count === 'number' && count > 0) {
      const numbers = node.values.slice(0, count);
      if (numbers.length < count || numbers.some((v) => typeof v !== 'number' || Number.isNaN(v))) {
        problems.push({ id: node.id, message: count === 2 ? 'Enter both numbers.' : 'Enter a number.' });
      } else if (count === 2 && numbers[0] > numbers[1]) {
        problems.push({ id: node.id, message: 'The first number must not be bigger than the second.' });
      }
    }
    return undefined;
  };
  visit(tree, 0);
  return problems;
};

// Immutable edits, by node id.
const mapTree = (node, fn) => {
  const mapped = fn(node);
  if (mapped.type !== 'group') return mapped;
  return { ...mapped, children: mapped.children.map((c) => mapTree(c, fn)) };
};

export const updateNode = (tree, nodeId, changes) => mapTree(tree, (n) => (n.id === nodeId ? { ...n, ...changes } : n));

export const removeNode = (tree, nodeId) => mapTree(tree, (n) => (
  n.type === 'group' ? { ...n, children: n.children.filter((c) => c.id !== nodeId) } : n
));

export const addChild = (tree, groupId, child) => mapTree(tree, (n) => (
  n.id === groupId ? { ...n, children: [...n.children, child] } : n
));

/** Choosing a question resets the operator to its first and clears the answers. */
export const setQuestion = (tree, nodeId, question) => updateNode(tree, nodeId, {
  questionId: question.question_id, operator: operatorsFor(question)[0] ?? null, values: [],
});

/** Changing the operator keeps the answers only while they still fit. */
export const setOperator = (tree, nodeId, operator, previous) => {
  const same = valueCount(operator) === valueCount(previous)
    || (NUMERIC_OPERATORS.has(operator) && NUMERIC_OPERATORS.has(previous));
  return updateNode(tree, nodeId, { operator, ...(same ? {} : { values: [] }) });
};

/**
 * The question list for the dropdown: usable questions only, each grid as a heading over its
 * usable rows; everything else hidden (decided 19 October 2026).
 */
export const questionMenu = (questions = []) => {
  const items = [];
  const rowsByGrid = new Map();
  for (const q of questions) {
    if (q.parent_question_id != null && q.usable_in_quotas) {
      if (!rowsByGrid.has(q.parent_question_id)) rowsByGrid.set(q.parent_question_id, []);
      rowsByGrid.get(q.parent_question_id).push(q);
    }
  }
  for (const q of questions) {
    if (q.parent_question_id != null) continue;
    if (q.kind === 'grid') {
      const rows = rowsByGrid.get(q.question_id) || [];
      if (rows.length) {
        items.push({ type: 'heading', key: `grid-${q.question_id}`, question: q });
        rows.forEach((row) => items.push({ type: 'question', key: `q-${row.question_id}`, question: row, indent: true }));
      }
    } else if (q.usable_in_quotas) {
      items.push({ type: 'question', key: `q-${q.question_id}`, question: q });
    }
  }
  return items;
};

// JSON with sorted keys: Wrangler's replies order keys differently from the model.
const canonical = (value) => {
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  if (value && typeof value === 'object') {
    return `{${Object.keys(value).sort().map((k) => `${JSON.stringify(k)}:${canonical(value[k])}`).join(',')}}`;
  }
  return JSON.stringify(value);
};

/** Is the model the same logic as Wrangler's tree? (For "you've changed the logic".) */
export const sameLogic = (tree, logic) => {
  try {
    return canonical(toLogic(tree)) === canonical(logic);
  } catch {
    return false;
  }
};
